import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  MenuItem,
  FormHelperText,
  Typography // Import Typography for consistent text styling
} from '@mui/material';
import { isMobileOnly } from 'react-device-detect';
import { styled } from '@mui/material/styles';

const CustomLabelInput = styled(InputLabel)(() => ({
  "& .MuiFormLabel-asterisk.MuiInputLabel-asterisk": {
    visibility: "hidden",
    "&::before": {
      content: '"*"',
      visibility: "visible",
    },
  },
}));

const MuiMultiSelectList = ({
  label,
  options,
  onChange,
  value,
  defaultValue = [],
  widthValue,
  fullWidth = false,
  customSx = {},
  required = false,
  errorMessage,
  MenuProps,
  fieldName,
  customStyle = null
}) => {
  const [internalSelectedValue, setInternalSelectedValue] = useState(
    value !== undefined ? value : defaultValue
  );

  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (value !== undefined && JSON.stringify(value) !== JSON.stringify(internalSelectedValue)) {
      setInternalSelectedValue(value);
    }
  }, [value]);

  const handleSelectChange = (event) => {
    const selectedValueArray = event.target.value;
    setInternalSelectedValue(selectedValueArray);
    onChange(fieldName, selectedValueArray);
    if (required && touched) {
      setError(event.target.value.length === 0);
    }
  };

  const handleClose = () => {
    if (required) {
      setTouched(true);
      if (!internalSelectedValue || internalSelectedValue.length === 0) {
        setError(true);
      } else {
        setError(false);
      }
    }
  };

  return (
    <FormControl
      sx={{
        width: fullWidth ? '100%' : (isMobileOnly ? (widthValue ? '100%' : 'auto') : '170px'),
        paddingRight: isMobileOnly && widthValue ? '20px' : '0px',
        ...customSx,
        ...(customStyle ? customStyle : {}),
        '& .MuiInputLabel-root': {
          color: 'var(--M3-sys-light-on-surface, #191C1C)',
          fontFamily: 'Inter',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '24px',
          letterSpacing: '0.5px'
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#00696D',
        }
      }}
      required={required}
      error={error}
    >
      <CustomLabelInput id="multi-select-field-label">{label}</CustomLabelInput>

      <Select
        labelId="multi-select-field-label"
        id="multi-select-field"
        multiple
        value={internalSelectedValue || []}
        onChange={handleSelectChange}
        onClose={handleClose}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => (
          <Typography
            sx={{
              flexGrow: 1,
              color: 'var(--M3-sys-light-on-surface, #191C1C)',
              fontFamily: 'Inter',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '24px',
              letterSpacing: '0.5px'
            }}
          >
            {options
              .filter(option => selected.includes(option.value))
              .map(option => option.label)
              .join(', ')}
          </Typography>
        )}
        sx={{
          width: fullWidth ? '100%' : (isMobileOnly ? '100%' : '170px'),
          color: '#6F7979', // This might be overridden by the Typography style in renderValue
          '.MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6F7979'
            }
          },
          '.MuiSelect-select': {
            // Apply the desired style directly to the selected value display area
            // when it's not focused, if the Typography component above isn't enough.
            // However, the Typography within renderValue should handle this.
            '&:focus': {
              borderColor: '#00696D'
            },
          }
        }}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            ...MenuProps?.PaperProps,
            sx: {
              ...MenuProps?.PaperProps?.sx,
              '& .MuiMenuItem-root .MuiListItemText-primary': {
                color: 'var(--M3-sys-light-on-surface, #191C1C) !important',
                fontFamily: 'Inter !important',
                fontSize: '16px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: '24px !important',
                letterSpacing: '0.5px !important'
              },
              '& .MuiMenuItem-root .MuiTypography-root': {
                color: 'var(--M3-sys-light-on-surface, #191C1C) !important',
                fontFamily: 'Inter !important',
                fontSize: '16px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: '24px !important',
                letterSpacing: '0.5px !important'
              }
            }
          }
        }}
      >
        {options?.map(({ label: optionLabel, value: optionValue }) => (
          <MenuItem key={optionValue} value={optionValue} dense sx={{
            padding: '8px 24px 8px 16px'
          }}>
            <Checkbox
              checked={internalSelectedValue.includes(optionValue)}
              sx={{
                color: '#6F7979',
                '&.Mui-checked': { color: '#00696D' },
                marginRight: '16px',
              }}
            />
            <ListItemText
              primary={optionLabel}
              sx={{ flexGrow: 1 }}
            />
          </MenuItem>
        ))}
      </Select>

      {error && (
        <FormHelperText>{errorMessage || 'Dette feltet er påkrevd'}</FormHelperText>
      )}
    </FormControl>
  );
};

export default MuiMultiSelectList;